import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { css } from "@emotion/react";
import Cloud from "@mui/icons-material/Cloud";
import Delete from "@mui/icons-material/Delete";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";
import Groups from "@mui/icons-material/Groups";
import Info from "@mui/icons-material/Info";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Visibility from "@mui/icons-material/Visibility";
import type { TooltipProps } from "@mui/material";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  tooltipClasses,
  Typography,
  useTheme,
} from "@mui/material";
import { Formik } from "formik";
import { debounce, isEqual } from "lodash-es";
import Papa from "papaparse";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import type { InferType } from "yup";
import { array, bool, boolean, object, string } from "yup";

import type {
  CreateOrganisationRequest,
  EmbeddedDashboard,
  ExtendedOrganisation,
  ExtendedThalamosUser,
  GetOrganisationResponse,
  OrganisationConfiguration,
  OrganisationMembership,
  OrgTree,
  RlsRule,
  SoadListResponse,
  TemplateData,
  UUID,
} from "@thalamos/aspire-common";
import {
  baseTemplates,
  canAmhpSearchS12Doctors,
  canAmhpTeamApproveS12Payments,
  canAmhpTeamUsePreciseS12Payments,
  canDoctorInitiateS12Payments,
  canDoctorInviteGuestS12Amhp,
  EmbeddedDashboardSchema,
  formValidationErrorMessages,
  getFullFormName,
  isCqc,
  isDoctorS12Searchable,
  isGuestUser,
  isValidEmail,
  mergeConfigurations,
  nonAdmissionTemplates,
  nonEmptyString,
  postalCodeValidationRule,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import { addOrgMemberValues } from "./constants.js";
import { ManagedLocationPicker } from "./ManagedLocationPicker.js";
import { ManageOrganisationUsers } from "./ManagingOrganisationUsers/ManageOrganisationUsers.js";
import { OrganisationPicker } from "./OrganisationPicker.js";
import { SupersetDashboardPicker } from "./SupersetDashboardPicker.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  Dropdown,
  MenuFlyout,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
  Typeahead,
} from "~/components/design-system/index.js";
import {
  ConfirmationCheckboxFormField,
  DropdownFormField,
  NameAddressFormField,
  TextboxFormField,
} from "~/components/form/index.js";
import type { DialogProps } from "~/hooks/dialogs/index.js";
import { useDialogs } from "~/hooks/dialogs/index.js";
import { getRoleLabel } from "~/util.js";

const orgDetailsSchema = object({
  name: nonEmptyString
    .default("")
    .required("Please enter a name")
    .trim()
    .max(255, formValidationErrorMessages.tooManyCharactersError),

  address: object({
    address: nonEmptyString
      .required("Address is required")
      .trim()
      .max(255, formValidationErrorMessages.tooManyCharactersError),
    postalCode: postalCodeValidationRule("role", "hospital"),
    isConfirmed: bool().required().isTrue("Please confirm your address"),
  }).default({
    address: "",
    isConfirmed: false,
    postalCode: "",
  }),

  role: nonEmptyString.default(null).nonNullable("Please enter a role"),

  roleName: string().default(""),

  isTrainingOrganisation: boolean().default(false),
  isUserManagementOrganisation: boolean().default(false),
  hasPatientMergeAccess: boolean().default(false),

  isOwnDataController: boolean().default(false),
  dataControllerId: nonEmptyString.default(null).when("isOwnDataController", {
    is: false,
    then: () =>
      nonEmptyString
        .required()
        .nonNullable("Please enter a data controller ID"),
    otherwise: () => nonEmptyString.default(null),
  }),

  dataControllerName: string().default(""),

  hasManagingOrganisation: boolean().default(true),

  managingOrganisationId: nonEmptyString
    .default(null)
    .when("hasManagingOrganisation", {
      is: true,
      then: () =>
        nonEmptyString
          .required()
          .nonNullable("Please enter a managing organisation"),
      otherwise: () => nonEmptyString.default(null),
    }),
  managingOrganisationName: string().default(""),

  // The authority whose rate card applies when this organisation's AMHP teams
  // organise an assessment. Optional: an organisation that runs no AMHP teams
  // never needs one.
  organisingLocalAuthorityId: string().nullable().default(null).optional(),

  // Don't allow to set ODS yet?
  codes: array()
    .of(
      object({
        system: nonEmptyString.required("System is required"),
        code: nonEmptyString.required("Code is required"),
      }),
    )
    .default([]),
});

type OrgDataType = Omit<CreateOrganisationRequest, "dataControllerId"> & {
  dataControllerId: string | null;
  dataControllerName: string | undefined;
  managingOrganisationName: string | undefined;
  hasManagingOrganisation: boolean;
  isOwnDataController: boolean;
  roleName: string;
};

/** Multi-select option values pack a pair of fields as `first*second`. */
function splitPackedOption(value: string): { first: string; second: string } {
  const parts = value.split("*");
  return { first: parts[0] ?? "", second: parts[1] ?? "" };
}

/**
 * Removes the override so the field inherits again: an explicit `undefined`
 * would spread over the inherited value rather than fall back to it.
 */
function withoutOverride(
  configuration: Partial<OrganisationConfiguration>,
  field: keyof OrganisationConfiguration,
): Partial<OrganisationConfiguration> {
  const { [field]: _removed, ...rest } = configuration;
  return rest;
}

const orgRoles = [
  { label: "NHS Trust", value: "nhs/trust" },
  { label: "Hospital", value: "hospital" },
  { label: "Local Authority", value: "nhs/local-authority" },
  { label: "NHS ICB", value: "nhs/icb" },
  { label: "NHS Commissioning Region", value: "nhs/commissioning-region" },
  { label: "NHS Dashboard", value: "nhs/dashboard" },
  { label: "Superset Dashboard", value: "superset/dashboard" },
];

function DeleteOrgButton({
  organisation,
}: {
  organisation: ExtendedOrganisation;
}) {
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  const navigate = useNavigate();

  return (
    <>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => {
            setConfirmFn(null);
          }}
        />
      )}

      <Button
        onClick={() => {
          setConfirmFn({
            message: `Are you sure you want to delete this organisation (${organisation.name})? This cannot be undone. The organisation must have no child organisations, teams or members, and cannot be the data controller for another org.`,
            confirmFn: async () => {
              const result = await api.organisations.delete(organisation.id, {
                reason: "created-in-error",
              });

              if (result.status === 204) {
                navigate(routeFns.adminOrganisationsPage());
                renderSuccessToast({
                  message: "Organisation deleted successfully",
                });
              } else {
                renderErrorToast({
                  message: `${result.data.reason}`,
                });
              }
            },
          });
        }}
        sx={{ ml: "1em" }}
        color={"error"}
        label={"Delete"}
      />
    </>
  );
}

function OrganisationCreateEditForm({
  organisation,
  organisationId,
  refetchOrg,
}: {
  organisation: ExtendedOrganisation | null;
  organisationId: string;
  refetchOrg: () => void;
}) {
  const dialogs = useDialogs();

  const isEditableOrg =
    !organisation || orgRoles.map((r) => r.value).includes(organisation.role);

  const initialData = {
    ...orgDetailsSchema.getDefault(),
    ...(organisation
      ? {
          ...organisation,
          hasPatientMergeAccess: organisation.hasPatientMergeAccess || false,
          isTrainingOrganisation: organisation.isTrainingOrganisation || false,
          isUserManagementOrganisation:
            organisation.isUserManagementOrganisation ?? false,
          roleName:
            orgRoles.find((r) => r.value === organisation.role)?.label ||
            organisation.role,
          managingOrganisationName: organisation.managingOrganisationName || "",
          hasManagingOrganisation: !!organisation.managingOrganisationId,
          isOwnDataController: organisation.dataControllerId === organisationId,
          address: {
            address: organisation.address,
            postalCode: organisation.postalCode,
            isConfirmed: true,
          },
        }
      : {}),
  };

  return isEditableOrg ? (
    <Formik<OrgDataType>
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const organisingLocalAuthorityId =
          values.organisingLocalAuthorityId || null;
        const cleanedOrgData = {
          name: values.name,
          address: {
            address: values.address.address,
            postalCode: values.address.postalCode,
            isConfirmed: true,
          },
          role: values.role,
          hasPatientMergeAccess: values.hasPatientMergeAccess,
          isTrainingOrganisation: values.isTrainingOrganisation,
          dataControllerId: values.isOwnDataController
            ? organisationId
            : values.dataControllerId!,
          managingOrganisationId: values.hasManagingOrganisation
            ? values.managingOrganisationId
            : null,
          codes: organisation?.codes || [],
          isUserManagementOrganisation: values.isUserManagementOrganisation,
          // Sent only when changed on this form: the form holds the value as
          // it was when it loaded, and sending that snapshot unchanged would
          // overwrite a newer organising authority saved since.
          ...(organisation === null ||
          organisingLocalAuthorityId !==
            (organisation.organisingLocalAuthorityId ?? null)
            ? { organisingLocalAuthorityId }
            : {}),
        };

        if (organisation) {
          // This is an update.

          // Did the Data Controller change?
          // If so, we need to confirm the change and capture approvers.
          let approvers = {};
          {
            const oldDataControllerId = organisation.dataControllerId;
            const newDataControllerId = cleanedOrgData.dataControllerId;
            if (oldDataControllerId !== newDataControllerId) {
              const result = await dialogs.open(
                ChangeDataControllerConfirmationDialog,
              );
              if (result.continue) {
                approvers = {
                  approverOne: result.approverOne,
                  approverTwo: result.approverTwo,
                  ccmTicket: result.ccmTicket,
                };
              } else {
                setSubmitting(false);
                return;
              }
            }
          }

          const result = await api.organisations.update(organisationId, {
            ...cleanedOrgData,
            ...approvers,
          });

          if (result.status === 204) {
            refetchOrg();
            renderSuccessToast({
              message: "Organisation updated successfully",
            });
          } else {
            renderErrorToast({
              message: `Organisation update failed: ${result.data}`,
            });
          }
        } else {
          const result = await api.organisations.create(
            organisationId,
            cleanedOrgData,
          );

          if (result.status === 204) {
            refetchOrg();
            renderSuccessToast({
              message: "Organisation created successfully",
            });
          } else {
            renderErrorToast({
              message: `Organisation create failed: ${result.data}`,
            });
          }
        }
        setSubmitting(false);
      }}
      validationSchema={orgDetailsSchema}
      initialValues={initialData as OrgDataType}
    >
      {(formikData) => {
        const {
          values,
          setValues,
          setFieldTouched,
          errors,
          touched,
          handleBlur,
          submitForm,
        } = formikData;

        const fieldProps = {
          validationSchema: orgDetailsSchema,
          values,
          setValues,
          setFieldTouched,
          errors,
          touched,
          context: {},
          handleBlur,
        };

        return (
          <>
            <Box sx={{ mb: 0 }}>
              <TextboxFormField
                field={{ type: "textbox", label: "Name (*)", field: "name" }}
                fieldProps={fieldProps}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <NameAddressFormField
                field={{
                  type: "name-address",
                  disableEmail: true,
                  disableName: true,
                  field: "address",
                  addressLabel: "Address (*)",
                }}
                fieldProps={fieldProps}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <ConfirmationCheckboxFormField
                field={{
                  type: "confirmation-checkbox",
                  label: "",
                  field: "hasManagingOrganisation",
                  checkboxLabel:
                    "Does this organisation have a managing organisation (it normally will)?",
                }}
                fieldProps={fieldProps}
              />
              {values.hasManagingOrganisation && (
                <OrganisationPicker
                  label={"Managing Organisation (*)"}
                  error={
                    touched.managingOrganisationId
                      ? (errors.managingOrganisationId as string)
                      : null
                  }
                  idField={"managingOrganisationId"}
                  nameField={"managingOrganisationName"}
                  values={values}
                  setValues={setValues}
                />
              )}
            </Box>
            <Box sx={{ mb: 1 }}>
              <DropdownFormField
                field={{
                  type: "dropdown",
                  label: "Role (*)",
                  valueField: "role",
                  textField: "roleName",
                  options: orgRoles,
                }}
                fieldProps={fieldProps}
              />
              <ManagedLocationPicker
                locationType="localAuthority"
                label={
                  "Billing local authority (rate card for this organisation's out-of-area assessments; add further local authorities from Lists)"
                }
                error={
                  touched.organisingLocalAuthorityId
                    ? (errors.organisingLocalAuthorityId as string)
                    : null
                }
                idField={"organisingLocalAuthorityId"}
                values={values}
                setValues={setValues}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <ConfirmationCheckboxFormField
                field={{
                  type: "confirmation-checkbox",
                  label: "",
                  field: "isOwnDataController",
                  checkboxLabel:
                    "Is this organisation its own data controller?",
                }}
                fieldProps={fieldProps}
              />
              <OrganisationPicker
                label={"Data Controller (*)"}
                error={
                  touched.dataControllerId
                    ? (errors.dataControllerId as string)
                    : null
                }
                idField={"dataControllerId"}
                nameField={"dataControllerName"}
                values={values}
                setValues={setValues}
                disabled={values.isOwnDataController}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    onChange={() =>
                      setValues({
                        ...values,
                        isTrainingOrganisation: !values.isTrainingOrganisation,
                      })
                    }
                    checked={values.isTrainingOrganisation}
                    disabled={!values.name.toLowerCase().includes("training")}
                  />
                }
                label={
                  "Training Org (Can only be enabled if the name of the organisation includes 'training')"
                }
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    onChange={() =>
                      setValues({
                        ...values,
                        isUserManagementOrganisation:
                          !values.isUserManagementOrganisation,
                      })
                    }
                    checked={values.isUserManagementOrganisation}
                  />
                }
                label={
                  "User Management Org (Does this organisation 'manage' the Users in teams and organisations below it in the organisation hierarchy) "
                }
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.hasPatientMergeAccess}
                    onChange={() =>
                      setValues({
                        ...values,
                        hasPatientMergeAccess: !values.hasPatientMergeAccess,
                      })
                    }
                  />
                }
                label={"Patient Merge Enabled"}
              />
            </Box>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Button
                label={organisation ? "Update" : "Create"}
                onClick={submitForm}
              />
              {organisation && <DeleteOrgButton organisation={organisation} />}
            </Box>
          </>
        );
      }}
    </Formik>
  ) : (
    <Box>
      This organisation (role: {organisation.role}, address:{" "}
      {organisation?.address} {organisation?.postalCode}) cannot be edited in
      the admin UI. Please talk to the dev team about modifying it.
    </Box>
  );
}

function DataControllerInfo({
  organisationData,
  organisation,
}: {
  organisationData: GetOrganisationResponse;
  organisation: ExtendedOrganisation;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <h3>Orgs in this data controller</h3>
      <TableContainer sx={{ width: "100%" }}>
        <TableRow>
          <TableCell>
            <strong>{t("common.name")}</strong>
          </TableCell>
        </TableRow>
        {organisationData &&
          (organisation?.orgsInThisDataController || []).map((org) => {
            return (
              <TableRow>
                <TableCell>{org.name}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      navigate(routeFns.organisationCreateEdit(org.id));
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
      </TableContainer>
    </>
  );
}

function AddSingleSoad({
  cqcOrganisationId,
  isOpen,
  close,
  refetchOrg,
}: {
  cqcOrganisationId: string;
  isOpen: boolean;
  close: () => void;
  refetchOrg: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  return (
    <PopupDialog open={isOpen} onClose={close}>
      <h4>Add Single SOAD</h4>
      <TextField
        name="soadName"
        label="Name"
        useFullWidth
        value={name}
        onChange={(e) => {
          setName(e);
        }}
      />

      <TextField
        name="soadEmail"
        label="Email"
        useFullWidth
        value={email}
        onChange={(e) => {
          setEmail(e);
        }}
      />

      <Button
        disabled={submitting}
        label="Add SOAD"
        onClick={async () => {
          setSubmitting(true);

          const result = await api.soadUserTeamOrg.create({
            name: name.trim(),
            email: email.trim().toLowerCase(),
            soadOrganisationId: v4(),
            soadTeamId: v4(),
            cqcOrganisationId,
          });

          if (result.status === 204) {
            renderSuccessToast({
              message: "SOAD created successfully",
            });
            refetchOrg();
            close();
          } else {
            renderErrorToast({
              message: `Failed to create SOAD: ${(result.data as any).message ?? "Unknown error"}`,
            });
          }

          setSubmitting(false);
        }}
      />
    </PopupDialog>
  );
}

function BulkAddSoad({
  soads,
  cqcOrganisationId,
  isOpen,
  close,
  refetchOrg,
}: {
  soads: SoadListResponse;
  cqcOrganisationId: string;
  isOpen: boolean;
  close: () => void;
  refetchOrg: () => void;
}) {
  const [rows, setRows] = useState<Record<string, string>[] | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /** A column's trimmed value; a column the uploaded CSV omits reads as empty. */
  function cell(row: Record<string, string>, column: string): string {
    return row[column]?.trim() ?? "";
  }

  function isRowValid(row: Record<string, string>): boolean {
    return (
      cell(row, "name") !== "" &&
      cell(row, "email") !== "" &&
      isValidEmail(cell(row, "email"))
    );
  }

  function isValidFile(rows: Record<string, string>[]): boolean {
    return rows.length > 0 && rows.every(isRowValid);
  }

  const duplicateRows =
    rows &&
    rows.filter((r) =>
      soads.soads.find(
        (s) =>
          s.soadEmail.trim().toLowerCase() === cell(r, "email").toLowerCase(),
      ),
    );

  return (
    <PopupDialog open={isOpen} onClose={close}>
      <h4>Bulk Add SOADs</h4>
      <input
        accept=".csv"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            Papa.parse(file, {
              header: true,
              skipEmptyLines: true,
              complete: (results) => {
                setRows(
                  (results.data as Record<string, string>[]).filter(
                    (r: any) =>
                      !Object.values(r).every(
                        (v) => (v as string).trim() === "",
                      ),
                  ),
                );
              },
              error: (error) => {
                renderErrorToast({
                  message: `Failed to parse CSV: ${error.message}`,
                });
              },
            });
          }
        }}
        type="file"
        capture="environment"
      />
      {rows && (
        <Box sx={{ mt: 2 }}>
          {!isValidFile(rows) ? (
            <Banner
              bannerType={BannerList.ERROR}
              title="Invalid CSV file. Each row must have a name and a valid email address."
            />
          ) : duplicateRows && duplicateRows.length > 0 ? (
            <Banner
              bannerType={BannerList.ERROR}
              title={`Emails that are already linked to this CQC is found: ${duplicateRows.map((r) => r.email).join(", ")}`}
            />
          ) : (
            <Banner
              bannerType={BannerList.INFO}
              title={`Found ${rows.length} rows. Click 'Confirm' to create them.`}
              leftButtonLabel="Confirm"
              disabledButtonLeft={submitting}
              onLeftButton={async () => {
                setSubmitting(true);

                const failed: Record<string, string>[] = [];

                for (const row of rows) {
                  const result = await api.soadUserTeamOrg.create({
                    name: cell(row, "name"),
                    email: cell(row, "email").toLowerCase(),
                    soadOrganisationId: v4(),
                    soadTeamId: v4(),
                    cqcOrganisationId,
                  });

                  if (result.status === 204) {
                  } else {
                    failed.push(row);
                  }
                }

                if (failed.length > 0) {
                  renderErrorToast({
                    message: `Failed to create ${failed.length} SOADs: ${failed.map((r) => `${r.name} (${r.email})`).join(", ")}`,
                  });
                } else {
                  renderSuccessToast({
                    message: "All SOADs created successfully",
                  });
                  close();
                  refetchOrg();
                }

                setSubmitting(false);
              }}
            />
          )}

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ backgroundColor: isRowValid(row) ? "inherit" : "red" }}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </PopupDialog>
  );
}

function SoadManagement({
  organisation,
  refetchOrg,
}: {
  organisationData: GetOrganisationResponse;
  organisation: ExtendedOrganisation;
  refetchOrg: () => void;
}) {
  const { t } = useTranslation();

  const [{ data: soads, loading: soadsLoading, response }] =
    apiHooks.soadUserTeamOrg.getSoads(organisation.id);

  const [isSingleSoadOpen, setIsSingleSoadOpen] = useState(false);
  const [_isBulkSoadOpen, setIsBulkSoadOpen] = useState(false);

  return soadsLoading || response?.status !== 200 ? (
    <>Loading org tree.</>
  ) : (
    <Container>
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <AddSingleSoad
          refetchOrg={refetchOrg}
          cqcOrganisationId={organisation.id}
          isOpen={isSingleSoadOpen}
          close={() => {
            setIsSingleSoadOpen(false);
          }}
        />
        <BulkAddSoad
          soads={soads!}
          refetchOrg={refetchOrg}
          cqcOrganisationId={organisation.id}
          isOpen={_isBulkSoadOpen}
          close={() => {
            setIsBulkSoadOpen(false);
          }}
        />
        <Button
          label="Add SOAD"
          onClick={() => {
            setIsSingleSoadOpen(true);
          }}
        />
        <Button
          label="Bulk Add SOADs"
          onClick={() => {
            setIsBulkSoadOpen(true);
          }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>{t("common.name")}</strong>
            </TableCell>
            <TableCell>
              <strong>{t("common.email")}</strong>
            </TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(soads?.soads || [])
            .sort((x, y) => x.name.localeCompare(y.name))
            .map((soad) => {
              return (
                <TableRow key={soad.id}>
                  <TableCell>{soad.name}</TableCell>
                  <TableCell>{soad.soadEmail}</TableCell>
                  <TableCell>
                    <Button
                      endIcon={ButtonIcon.delete}
                      label="Delete"
                      onClick={async () => {
                        const confirmed = window.confirm(
                          "Are you sure you want to delete this SOAD?",
                        );
                        if (confirmed) {
                          await api.soadUserTeamOrg.delete({
                            soadTeamId: soad.teamId,
                            cqcOrganisationId: organisation.id,
                            soadUserId: soad.soadUserId,
                          });
                          refetchOrg();
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </Container>
  );
}

function ChildOrgsAndTeamsEdit({
  organisationData,
  organisation,
}: {
  organisationData: GetOrganisationResponse;
  organisation: ExtendedOrganisation;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <h3>Child Orgs</h3>
      <TableContainer sx={{ width: "100%" }}>
        <TableRow>
          <TableCell>
            <strong>{t("common.name")}</strong>
          </TableCell>
        </TableRow>
        {organisationData &&
          (organisation?.childOrganisations || []).map((org) => {
            return (
              <TableRow>
                <TableCell>{org.name}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      navigate(routeFns.organisationCreateEdit(org.id));
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
      </TableContainer>

      <h3>Teams</h3>
      <TableContainer sx={{ width: "100%" }}>
        <TableRow>
          <TableCell>
            <strong>{t("common.name")}</strong>
          </TableCell>
          <TableCell>
            <strong>Type</strong>
          </TableCell>
        </TableRow>
        {organisationData &&
          (organisation?.teams || []).map((team) => {
            return (
              <TableRow>
                <TableCell>{team.name}</TableCell>
                <TableCell>{team.type}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      navigate(routeFns.teamCreateEdit(team.id));
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
      </TableContainer>
    </>
  );
}

function OverrideableBooleanConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={configuration[field] !== undefined}
              onChange={() => {
                if (configuration[field] === undefined) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  setConfiguration(withoutOverride(configuration, field));
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Checkbox
              disabled={configuration[field] === undefined}
              checked={
                configuration[field] === undefined
                  ? (inheritedConfig[field] as boolean)
                  : (configuration[field] as boolean)
              }
              onChange={() => {
                setConfiguration({
                  ...configuration,
                  [field]: !configuration[field],
                });
              }}
            />
          }
          label={"Enable Feature"}
        />
      </TableCell>
    </TableRow>
  );
}

// Single source of truth for the Dr Finder capability labels, shared between the
// checkbox controls and the inherited-summary text so the two cannot drift.
const S12_AMHP_CAPABILITY_LABELS = {
  search: "DrFinder Search",
  approve: "Approve Payment Requests",
  precise: "Precise payment fees",
} as const;

const S12_DOCTOR_CAPABILITY_LABELS = {
  searchable: "Doctor is searchable",
  initiate: "Initiate payment requests",
  inviteGuest: "Invite guest AMHP",
} as const;

/**
 * Dr Finder — AMHP user settings (role-targeted). A single "Override" switch
 * governs whether this org sets the group locally or inherits it. When set, the
 * "Enable AMHP user settings" checkbox reveals a multi-select of capabilities;
 * "Precise payment fees" is nested under "Approve Payment Requests".
 *
 * The legacy flags (section12DoctorFinderEnabled / section12PaymentsEnabled /
 * section12PrecisePaymentsEnabled) are intentionally left untouched — the
 * accessor helpers in aspire-common fall back to them until these are set.
 */
export function OverrideableS12AmhpSettingsConfiguration({
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const isSet = configuration.section12AmhpSettingsEnabled !== undefined;
  const source = isSet ? configuration : inheritedConfig;
  const settingsEnabled = source.section12AmhpSettingsEnabled ?? false;
  const searchEnabled = source.section12AmhpSearchEnabled ?? false;
  const approveEnabled = source.section12AmhpApprovePaymentsEnabled ?? false;
  const preciseEnabled = source.section12AmhpPrecisePaymentsEnabled ?? false;

  const update = (patch: Partial<OrganisationConfiguration>) => {
    setConfiguration({ ...configuration, ...patch });
  };

  // The inherited summary (shown when this org is not overriding the group)
  // reflects only the *new role-targeted group's* inherited state. It reads
  // "off" unless an ancestor has explicitly enabled the new group — legacy
  // flags are a separate concern shown in the dedicated "Legacy" rows, and must
  // not be folded in here (turning Override off should not display everything as
  // on just because the parent still carries the legacy flags).
  const inheritedSummary = settingsEnabled
    ? [
        searchEnabled && S12_AMHP_CAPABILITY_LABELS.search,
        approveEnabled && S12_AMHP_CAPABILITY_LABELS.approve,
        approveEnabled && preciseEnabled && S12_AMHP_CAPABILITY_LABELS.precise,
      ]
        .filter(Boolean)
        .join(", ") || "no capabilities"
    : "off";

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>Dr Finder — AMHP user settings</span>
          <Chip label="New" size="small" color="primary" />
        </Stack>
      </TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  // Seed the new capabilities from this org's *effective*
                  // (legacy-derived) config so enabling the group preserves
                  // current behaviour rather than forcing everything off. The
                  // effective config merges this org's own overrides over the
                  // inherited hierarchy, and we read it through the same
                  // accessor helpers used at runtime.
                  // inheritedConfig is the fully-merged parent config, so the
                  // legacy flags are always present once this org's overrides
                  // are layered on — assert the complete shape for the helpers.
                  const effective = {
                    ...inheritedConfig,
                    ...configuration,
                  } as OrganisationConfiguration;
                  update({
                    section12AmhpSettingsEnabled: true,
                    section12AmhpSearchEnabled:
                      canAmhpSearchS12Doctors(effective),
                    section12AmhpApprovePaymentsEnabled:
                      canAmhpTeamApproveS12Payments(effective),
                    section12AmhpPrecisePaymentsEnabled:
                      canAmhpTeamUsePreciseS12Payments(effective),
                  });
                } else {
                  const temp = { ...configuration };
                  delete temp.section12AmhpSettingsEnabled;
                  delete temp.section12AmhpSearchEnabled;
                  delete temp.section12AmhpApprovePaymentsEnabled;
                  delete temp.section12AmhpPrecisePaymentsEnabled;
                  setConfiguration(temp);
                }
              }}
            />
          }
          label="Override"
        />
      </TableCell>
      <TableCell>
        {!isSet ? (
          <Box>{`Inherited: ${inheritedSummary}`}</Box>
        ) : (
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={settingsEnabled}
                  onChange={() => {
                    if (settingsEnabled) {
                      // Turning the group off clears all capabilities so the
                      // explicit `false` overrides any legacy flag via `??`.
                      update({
                        section12AmhpSettingsEnabled: false,
                        section12AmhpSearchEnabled: false,
                        section12AmhpApprovePaymentsEnabled: false,
                        section12AmhpPrecisePaymentsEnabled: false,
                      });
                    } else {
                      update({ section12AmhpSettingsEnabled: true });
                    }
                  }}
                />
              }
              label="Enable AMHP user settings"
            />
            {settingsEnabled && (
              <Stack spacing={0.5} sx={{ pl: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchEnabled}
                      onChange={() => {
                        update({ section12AmhpSearchEnabled: !searchEnabled });
                      }}
                    />
                  }
                  label={S12_AMHP_CAPABILITY_LABELS.search}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approveEnabled}
                      onChange={() => {
                        if (approveEnabled) {
                          // Turning approve off clears precise too.
                          update({
                            section12AmhpApprovePaymentsEnabled: false,
                            section12AmhpPrecisePaymentsEnabled: false,
                          });
                        } else {
                          update({
                            section12AmhpApprovePaymentsEnabled: true,
                          });
                        }
                      }}
                    />
                  }
                  label={S12_AMHP_CAPABILITY_LABELS.approve}
                />
                {approveEnabled && (
                  <FormControlLabel
                    sx={{ pl: 3 }}
                    control={
                      <Checkbox
                        checked={preciseEnabled}
                        onChange={() => {
                          update({
                            section12AmhpPrecisePaymentsEnabled:
                              !preciseEnabled,
                          });
                        }}
                      />
                    }
                    label={S12_AMHP_CAPABILITY_LABELS.precise}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * Dr Finder — Doctor user settings (role-targeted). Mirrors the AMHP group: a
 * single "Override" switch, then an "Enable Doctor user settings" checkbox that
 * reveals the multi-select of doctor capabilities.
 */
export function OverrideableS12DoctorSettingsConfiguration({
  inheritedConfig,
  configuration,
  setConfiguration,
  isGuestOrg,
}: {
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
  // "Invite guest AMHP" must never be offered for a guest org (a guest can't
  // be a sharing source for another guest AMHP).
  isGuestOrg: boolean;
}) {
  const isSet = configuration.section12DoctorSettingsEnabled !== undefined;
  const source = isSet ? configuration : inheritedConfig;
  const settingsEnabled = source.section12DoctorSettingsEnabled ?? false;
  const searchableEnabled = source.section12DoctorSearchableEnabled ?? false;
  const initiateEnabled =
    source.section12DoctorInitiatePaymentsEnabled ?? false;
  const inviteGuestEnabled =
    source.section12DoctorInviteGuestAmhpEnabled ?? false;

  const update = (patch: Partial<OrganisationConfiguration>) => {
    setConfiguration({ ...configuration, ...patch });
  };

  // Reflect only the new role-targeted group's inherited state — "off" unless an
  // ancestor explicitly enabled the new group. Legacy flags are shown in the
  // dedicated "Legacy" rows and must not be folded in here.
  const inheritedSummary = settingsEnabled
    ? [
        searchableEnabled && S12_DOCTOR_CAPABILITY_LABELS.searchable,
        initiateEnabled && S12_DOCTOR_CAPABILITY_LABELS.initiate,
        inviteGuestEnabled &&
          !isGuestOrg &&
          S12_DOCTOR_CAPABILITY_LABELS.inviteGuest,
      ]
        .filter(Boolean)
        .join(", ") || "no capabilities"
    : "off";

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>Dr Finder — Doctor user settings</span>
          <Chip label="New" size="small" color="primary" />
        </Stack>
      </TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  // Seed from this org's effective (legacy-derived) config so
                  // enabling the group preserves current behaviour instead of
                  // forcing doctor searchability / payment initiation off.
                  // inheritedConfig is the fully-merged parent config, so the
                  // legacy flags are always present once this org's overrides
                  // are layered on — assert the complete shape for the helpers.
                  const effective = {
                    ...inheritedConfig,
                    ...configuration,
                  } as OrganisationConfiguration;
                  update({
                    section12DoctorSettingsEnabled: true,
                    section12DoctorSearchableEnabled:
                      isDoctorS12Searchable(effective),
                    section12DoctorInitiatePaymentsEnabled:
                      canDoctorInitiateS12Payments(effective),
                    section12DoctorInviteGuestAmhpEnabled:
                      canDoctorInviteGuestS12Amhp(effective),
                  });
                } else {
                  const temp = { ...configuration };
                  delete temp.section12DoctorSettingsEnabled;
                  delete temp.section12DoctorSearchableEnabled;
                  delete temp.section12DoctorInitiatePaymentsEnabled;
                  delete temp.section12DoctorInviteGuestAmhpEnabled;
                  setConfiguration(temp);
                }
              }}
            />
          }
          label="Override"
        />
      </TableCell>
      <TableCell>
        {!isSet ? (
          <Box>{`Inherited: ${inheritedSummary}`}</Box>
        ) : (
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={settingsEnabled}
                  onChange={() => {
                    if (settingsEnabled) {
                      update({
                        section12DoctorSettingsEnabled: false,
                        section12DoctorSearchableEnabled: false,
                        section12DoctorInitiatePaymentsEnabled: false,
                        section12DoctorInviteGuestAmhpEnabled: false,
                      });
                    } else {
                      update({ section12DoctorSettingsEnabled: true });
                    }
                  }}
                />
              }
              label="Enable Doctor user settings"
            />
            {settingsEnabled && (
              <Stack spacing={0.5} sx={{ pl: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchableEnabled}
                      onChange={() => {
                        update({
                          section12DoctorSearchableEnabled: !searchableEnabled,
                        });
                      }}
                    />
                  }
                  label={S12_DOCTOR_CAPABILITY_LABELS.searchable}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={initiateEnabled}
                      onChange={() => {
                        update({
                          section12DoctorInitiatePaymentsEnabled:
                            !initiateEnabled,
                          // "Invite guest AMHP" is a child of "Initiate payment
                          // requests" — clear it when its parent is turned off.
                          ...(initiateEnabled
                            ? { section12DoctorInviteGuestAmhpEnabled: false }
                            : {}),
                        });
                      }}
                    />
                  }
                  label={S12_DOCTOR_CAPABILITY_LABELS.initiate}
                />
                {initiateEnabled && !isGuestOrg && (
                  <Stack sx={{ pl: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={inviteGuestEnabled}
                          onChange={() => {
                            update({
                              section12DoctorInviteGuestAmhpEnabled:
                                !inviteGuestEnabled,
                            });
                          }}
                        />
                      }
                      label={S12_DOCTOR_CAPABILITY_LABELS.inviteGuest}
                    />
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableFormTemplateConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const isSet = configuration[field] !== undefined;

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  setConfiguration(withoutOverride(configuration, field));
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        <Dropdown
          onChange={(values: string[]) => {
            const newTemplates = values
              .map((v) => {
                const packed = splitPackedOption(v);
                return { id: packed.first, version: packed.second };
              })
              .sort((a, b) => (a.id < b.id ? -1 : 1));
            setConfiguration({
              ...configuration,
              [field]: newTemplates,
            });
          }}
          disabled={!isSet}
          name={"formTemplates"}
          selectedValue={(isSet
            ? configuration.enabledForms || []
            : inheritedConfig.enabledForms || []
          ).map((f) => `${f.id}*${f.version}`)}
          multiple
          values={nonAdmissionTemplates
            .sort((a, b) => (a.id < b.id ? -1 : 1))
            .map((t) => ({
              value: `${t.id}*${t.version}`,
              label: `${t.formName} (${t.version})`,
            }))}
        />
      </TableCell>
    </TableRow>
  );
}

function OverrideableTermsAndConditionsConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const isSet = configuration[field] !== undefined;

  const [{ data, loading: loadingTermsAndConditions, error }] =
    apiHooks.termsAndConditions.getAll();

  const termsAndConditions = data?.termsAndConditions || [];
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              disabled={error !== null}
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  setConfiguration(withoutOverride(configuration, field));
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {error ? (
          <p>Unable to update terms and conditions, try again later</p>
        ) : loadingTermsAndConditions ? (
          <CircularProgress />
        ) : (
          <Dropdown
            onChange={(value: string) => {
              const matched = termsAndConditions.find((t) => t.id === value);

              setConfiguration({
                ...configuration,
                [field]: {
                  id: matched?.id,
                  humanReadableName: matched?.humanReadableName,
                  type: matched?.type,
                },
              });
            }}
            disabled={!isSet}
            name={"termsAndConditions"}
            selectedValue={
              isSet
                ? (configuration.termsAndConditions?.id ?? "")
                : (inheritedConfig.termsAndConditions?.id ?? "")
            }
            values={termsAndConditions.map((t) => ({
              value: `${t.id}`,
              label: `${t.humanReadableName}`,
            }))}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableRioInstancesConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const isSet = configuration[field] !== undefined;

  const rioInstancesPageSize = 100;
  const page = 0;
  const [{ data, loading: loadingRioInstances, error }] = apiHooks.rio.search(
    "",
    rioInstancesPageSize,
    page * rioInstancesPageSize,
  );

  const rioInstances = data?.results || [];
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              disabled={error !== null}
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  setConfiguration(withoutOverride(configuration, field));
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {error ? (
          <p>Unable to update rio instances, try again later</p>
        ) : loadingRioInstances ? (
          <CircularProgress />
        ) : (
          <Dropdown
            onChange={(values: string[]) => {
              const newTemplates = values
                .map((v) => {
                  const packed = splitPackedOption(v);
                  return {
                    instanceId: packed.first,
                    displayName: packed.second,
                  };
                })
                .sort((a, b) => (a.displayName < b.displayName ? -1 : 1));
              setConfiguration({
                ...configuration,
                [field]: newTemplates,
              });
            }}
            disabled={!isSet}
            name={"rioInstances"}
            selectedValue={(isSet
              ? configuration.rioInstancesEnabled || []
              : inheritedConfig.rioInstancesEnabled || []
            ).map((f) => `${f.instanceId}*${f.displayName}`)}
            multiple
            values={rioInstances
              .sort((a, b) => (a.displayName < b.displayName ? -1 : 1))
              .map((t) => ({
                value: `${t.instanceId}*${t.displayName}`,
                label: `${t.displayName}`,
              }))}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableTppInstancesConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const isSet = configuration[field] !== undefined;

  const tppInstancesPageSize = 100;
  const page = 0;
  const [{ data, loading: loadingTppInstances, error }] = apiHooks.tpp.search(
    "",
    tppInstancesPageSize,
    page * tppInstancesPageSize,
  );

  const tppInstances = data?.results || [];
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              disabled={error !== null}
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  setConfiguration(withoutOverride(configuration, field));
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {error ? (
          <p>Unable to update TPP instances, try again later</p>
        ) : loadingTppInstances ? (
          <CircularProgress />
        ) : (
          <Dropdown
            onChange={(values: string[]) => {
              const newInstances = values
                .map((v) => {
                  const packed = splitPackedOption(v);
                  return {
                    instanceId: packed.first,
                    displayName: packed.second,
                  };
                })
                .sort((a, b) => (a.displayName < b.displayName ? -1 : 1));
              setConfiguration({
                ...configuration,
                [field]: newInstances,
              });
            }}
            disabled={!isSet}
            name={"tppInstances"}
            selectedValue={(isSet
              ? configuration.tppInstancesEnabled || []
              : inheritedConfig.tppInstancesEnabled || []
            ).map((f) => `${f.instanceId}*${f.displayName}`)}
            multiple
            values={tppInstances
              .sort((a, b) => (a.displayName < b.displayName ? -1 : 1))
              .map((t) => ({
                value: `${t.instanceId}*${t.displayName}`,
                label: `${t.displayName}`,
              }))}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableGenericInstancesConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const isSet = configuration[field] !== undefined;

  const genericInstancesPageSize = 100;
  const page = 0;
  const [{ data, loading: loadingGenericInstances, error }] =
    apiHooks.generic.search(
      "",
      genericInstancesPageSize,
      page * genericInstancesPageSize,
    );

  const genericInstances = data?.results || [];
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              disabled={error !== null}
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  const { [field]: _omitted, ...rest } = configuration;
                  setConfiguration({ ...rest });
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {error ? (
          <p>Unable to update generic instances, try again later</p>
        ) : loadingGenericInstances ? (
          <CircularProgress />
        ) : (
          <Dropdown
            onChange={(values: string[]) => {
              const newInstances = values
                .map((v) => {
                  const packed = splitPackedOption(v);
                  return {
                    instanceId: packed.first,
                    displayName: packed.second,
                  };
                })
                .sort((a, b) => (a.displayName < b.displayName ? -1 : 1));
              setConfiguration({
                ...configuration,
                [field]: newInstances,
              });
            }}
            disabled={!isSet}
            name={"genericInstances"}
            selectedValue={(isSet
              ? configuration.genericInstancesEnabled || []
              : inheritedConfig.genericInstancesEnabled || []
            ).map((f) => `${f.instanceId}*${f.displayName}`)}
            multiple
            values={genericInstances
              .sort((a, b) => (a.displayName < b.displayName ? -1 : 1))
              .map((t) => ({
                value: `${t.instanceId}*${t.displayName}`,
                label: t.displayName,
              }))}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableMhlAutoRouteConfiguration({
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const field = "mhlAutoRouteConfig" as const;
  const isSet = configuration[field] !== undefined;
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [searchedTeams, setSearchedTeams] = useState<
    { id: string; name: string; type: string }[]
  >([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedTeamDetails, setSelectedTeamDetails] = useState<
    Map<string, { name: string; type: string }>
  >(new Map());
  const [formTemplateSearchQuery, setFormTemplateSearchQuery] = useState("");

  const currentConfig = isSet ? configuration[field] : inheritedConfig[field];
  const selectedTeamIds = currentConfig?.teams?.map((t) => t.teamId) || [];
  const selectedFormTemplateIds = currentConfig?.enabledFormTemplates || [];

  // Form template IDs that do NOT require a hospital address
  const formsWithoutHospitalIds = [
    "mha-a3",
    "mha-a4",
    "mha-a7",
    "mha-a8",
    "mha-a11",
    "mha-cto1",
    "mha-cto2",
    "mha-cto9",
    "mha-cto12",
    "mha-s62",
    "mha-t2",
    "mha-t4",
    // SOAD forms (no hospital required)
    "mha-soad-t2",
    "mha-soad-t3",
    "mha-soad-t4",
    "mha-soad-t5",
    "mha-soad-t6",
    "mha-soad-cto11",
  ];

  // Fetch teams when search query changes
  useEffect(() => {
    const fetchTeams = async () => {
      if (!teamSearchQuery || teamSearchQuery.length < 2) {
        setSearchedTeams([]);
        return;
      }
      setLoadingTeams(true);
      try {
        const result = await api.teams.search(teamSearchQuery, 20, 0);
        const teams = result.data.results
          .filter((team) => team.status === "active")
          .map((team) => ({
            id: team.id,
            name: team.name,
            type: team.type,
          }));
        setSearchedTeams(teams);
      } catch {
        setSearchedTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };
    const debounced = setTimeout(fetchTeams, 300);
    return () => {
      clearTimeout(debounced);
    };
  }, [teamSearchQuery]);

  // Fetch details for already-selected teams
  useEffect(() => {
    const fetchSelectedTeamDetails = async () => {
      const missingTeamIds = selectedTeamIds.filter(
        (id) => !selectedTeamDetails.has(id),
      );
      if (missingTeamIds.length === 0) return;

      for (const teamId of missingTeamIds) {
        try {
          const result = await api.teams.get(teamId);
          if (result.data) {
            setSelectedTeamDetails((prev) => {
              const next = new Map(prev);
              next.set(teamId, {
                name: result.data!.name,
                type: result.data!.type,
              });
              return next;
            });
          }
        } catch {
          // Team may have been deleted
        }
      }
    };
    fetchSelectedTeamDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedTeamIds)]);

  const handleAddTeam = (teamId: string) => {
    if (!isSet || selectedTeamIds.includes(teamId)) return;
    const team = searchedTeams.find((t) => t.id === teamId);
    if (team) {
      setSelectedTeamDetails((prev) => {
        const next = new Map(prev);
        next.set(teamId, { name: team.name, type: team.type });
        return next;
      });
    }
    setConfiguration({
      ...configuration,
      [field]: {
        enabledFormTemplates: selectedFormTemplateIds,
        teams: [...(currentConfig?.teams || []), { teamId }],
      },
    });
  };

  const handleRemoveTeam = (teamId: string) => {
    if (!isSet) return;
    setConfiguration({
      ...configuration,
      [field]: {
        enabledFormTemplates: selectedFormTemplateIds,
        teams: (currentConfig?.teams || []).filter((t) => t.teamId !== teamId),
      },
    });
  };

  // Get form template options from baseTemplates
  const formTemplateOptions = baseTemplates.map((template) => ({
    id: template.id,
    label: getFullFormName(template),
  }));

  // Filter out already selected templates for typeahead options
  const availableFormTemplates = formTemplateOptions.filter(
    (t) => !selectedFormTemplateIds.includes(t.id),
  );

  const handleAddFormTemplate = (templateId: string) => {
    if (!isSet || selectedFormTemplateIds.includes(templateId)) return;
    setConfiguration({
      ...configuration,
      [field]: {
        enabledFormTemplates: [...selectedFormTemplateIds, templateId],
        teams: currentConfig?.teams || [],
      },
    });
  };

  const handleRemoveFormTemplate = (templateId: string) => {
    if (!isSet) return;
    setConfiguration({
      ...configuration,
      [field]: {
        enabledFormTemplates: selectedFormTemplateIds.filter(
          (id) => id !== templateId,
        ),
        teams: currentConfig?.teams || [],
      },
    });
  };

  const handleSelectAllFormTemplates = () => {
    if (!isSet) return;
    setConfiguration({
      ...configuration,
      [field]: {
        enabledFormTemplates: formTemplateOptions.map((t) => t.id),
        teams: currentConfig?.teams || [],
      },
    });
  };

  const handleSelectFormsWithoutHospital = () => {
    if (!isSet) return;
    const withoutHospital = formTemplateOptions
      .filter((t) => formsWithoutHospitalIds.includes(t.id))
      .map((t) => t.id);
    setConfiguration({
      ...configuration,
      [field]: {
        enabledFormTemplates: withoutHospital,
        teams: currentConfig?.teams || [],
      },
    });
  };

  const handleClearFormTemplates = () => {
    if (!isSet) return;
    setConfiguration({
      ...configuration,
      [field]: {
        enabledFormTemplates: [],
        teams: currentConfig?.teams || [],
      },
    });
  };

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field] ?? {
                      enabledFormTemplates: [],
                      teams: [],
                    },
                  });
                } else {
                  setConfiguration(withoutOverride(configuration, field));
                }
              }}
            />
          }
          label="Override"
        />
      </TableCell>
      <TableCell>
        <Stack spacing={2}>
          <Typography variant="subtitle2">Enabled Form Templates</Typography>
          <Typography variant="caption" color="textSecondary">
            Select which form templates trigger auto-routing after Part 1
            signature:
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              label="All"
              outlined
              small
              disabled={!isSet}
              onClick={handleSelectAllFormTemplates}
            />
            <Button
              label="Forms without hospital"
              outlined
              small
              disabled={!isSet}
              onClick={handleSelectFormsWithoutHospital}
            />
            <Button
              label="Clear"
              outlined
              small
              disabled={!isSet}
              onClick={handleClearFormTemplates}
            />
          </Stack>

          <Typeahead<{ id: string; label: string }>
            name="formTemplateSearch"
            placeholder="Search for form templates..."
            disabled={!isSet}
            options={availableFormTemplates}
            optionsKey="label"
            resetKey={selectedFormTemplateIds.join("-")}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.label || ""
            }
            inputValue={formTemplateSearchQuery}
            onInputChange={(value: string) => {
              const matchedTemplate = availableFormTemplates.find(
                (t) => t.label === value,
              );
              if (matchedTemplate) {
                handleAddFormTemplate(matchedTemplate.id);
                setTimeout(() => {
                  setFormTemplateSearchQuery("");
                }, 0);
              } else {
                setFormTemplateSearchQuery(value);
              }
            }}
            allowFreeText={false}
            enableFuse={true}
          />

          {selectedFormTemplateIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {selectedFormTemplateIds.map((templateId) => {
                const template = formTemplateOptions.find(
                  (t) => t.id === templateId,
                );
                return (
                  <Chip
                    key={templateId}
                    label={template?.label || templateId}
                    size="small"
                    onDelete={
                      isSet
                        ? () => {
                            handleRemoveFormTemplate(templateId);
                          }
                        : undefined
                    }
                  />
                );
              })}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2">Teams to Route To</Typography>
          <Typography variant="caption" color="textSecondary">
            Search and select teams to automatically receive forms:
          </Typography>

          <Typeahead<{ id: string; name: string; type: string }>
            name="teamSearch"
            placeholder="Search for teams..."
            disabled={!isSet}
            loading={loadingTeams}
            options={searchedTeams}
            optionsKey="name"
            resetKey={selectedTeamIds.join("-")}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option?.name
                  ? `${option.name} (${option.type || "unknown"})`
                  : ""
            }
            inputValue={teamSearchQuery}
            onInputChange={(value: string) => {
              // Check if this matches a team option label (selection made)
              const matchedTeam = searchedTeams.find(
                (team) => `${team.name} (${team.type || "unknown"})` === value,
              );
              if (matchedTeam) {
                handleAddTeam(matchedTeam.id);
                // Delay clearing to avoid flickering from React state updates
                setTimeout(() => {
                  setTeamSearchQuery("");
                  setSearchedTeams([]);
                }, 0);
              } else {
                setTeamSearchQuery(value);
              }
            }}
            allowFreeText={false}
            enableFuse={false}
          />

          {selectedTeamIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {selectedTeamIds.map((teamId) => {
                const details = selectedTeamDetails.get(teamId);
                return (
                  <Box
                    key={teamId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "action.selected",
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      {details?.name || teamId}
                      {details?.type && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="textSecondary"
                          sx={{ ml: 0.5 }}
                        >
                          ({details.type})
                        </Typography>
                      )}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleRemoveTeam(teamId);
                      }}
                      disabled={!isSet}
                      sx={{ ml: 0.5 }}
                    >
                      ×
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

/**
 * Parse a comma-separated string of IDs into a trimmed array,
 * returning undefined when no valid IDs are present.
 */
export function parseCommaSeparatedIds(raw: string): string[] | undefined {
  const ids = raw
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  return ids.length > 0 ? ids : undefined;
}

function GeographicUnitIdsField({
  namePrefix,
  index,
  value,
  onChange,
}: {
  namePrefix: string;
  index: number;
  value: string[];
  onChange: (ids: string[] | undefined) => void;
}) {
  const [text, setText] = useState(value.join(", "));

  // Sync from parent when the saved value changes externally (e.g. reset)
  const serialised = value.join(", ");
  useEffect(() => {
    setText(serialised);
  }, [serialised]);

  return (
    <TextField
      name={`${namePrefix}.rlsRules.${String(index)}.geographicUnitIds`}
      label="Geographic Unit IDs (comma-separated, optional)"
      value={text}
      useFullWidth
      onChange={(v: string) => {
        setText(v);
        onChange(parseCommaSeparatedIds(v));
      }}
    />
  );
}

/**
 * A single RLS rule row that owns its own search query state,
 * preventing cross-talk between multiple rows of the same type.
 */
function RlsRuleRow({
  rule,
  index,
  currentRules,
  onChangeRules,
  onRemove,
  namePrefix,
  keyPrefix,
  orgDetailsCache,
  setOrgDetailsCache,
  teamDetailsCache,
  setTeamDetailsCache,
}: {
  rule: RlsRule;
  index: number;
  currentRules: RlsRule[];
  onChangeRules: (rules: RlsRule[]) => void;
  onRemove: () => void;
  namePrefix: string;
  keyPrefix: string;
  orgDetailsCache: Map<string, { name: string }>;
  setOrgDetailsCache: Dispatch<SetStateAction<Map<string, { name: string }>>>;
  teamDetailsCache: Map<string, { name: string }>;
  setTeamDetailsCache: Dispatch<SetStateAction<Map<string, { name: string }>>>;
}) {
  // Per-row search state — avoids shared query across multiple rows
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [searchedOrgs, setSearchedOrgs] = useState<
    { id: string; name: string }[]
  >([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [searchedTeams, setSearchedTeams] = useState<
    { id: string; name: string }[]
  >([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const debouncedOrgSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        setLoadingOrgs(true);
        try {
          const result = await api.organisations.search(query, 20, 0);
          setSearchedOrgs(
            result.data.results.map((o) => ({ id: o.id, name: o.name })),
          );
        } catch {
          setSearchedOrgs([]);
        } finally {
          setLoadingOrgs(false);
        }
      }, 300),
    [],
  );

  useEffect(() => {
    if (!orgSearchQuery || orgSearchQuery.length < 2) {
      setSearchedOrgs([]);
      return;
    }
    void debouncedOrgSearch(orgSearchQuery);
    return () => {
      debouncedOrgSearch.cancel();
    };
  }, [orgSearchQuery, debouncedOrgSearch]);

  const debouncedTeamSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        setLoadingTeams(true);
        try {
          const result = await api.teams.search(query, 20, 0);
          setSearchedTeams(
            result.data.results.map((t) => ({ id: t.id, name: t.name })),
          );
        } catch {
          setSearchedTeams([]);
        } finally {
          setLoadingTeams(false);
        }
      }, 300),
    [],
  );

  useEffect(() => {
    if (!teamSearchQuery || teamSearchQuery.length < 2) {
      setSearchedTeams([]);
      return;
    }
    void debouncedTeamSearch(teamSearchQuery);
    return () => {
      debouncedTeamSearch.cancel();
    };
  }, [teamSearchQuery, debouncedTeamSearch]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        alignItems: "flex-start",
        mb: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
        }}
      >
        <Dropdown
          name={`${namePrefix}.rlsRules.${String(index)}.type`}
          values={[
            { label: "Force", value: "force" },
            { label: "Organisation", value: "organisation" },
            { label: "Team", value: "team" },
          ]}
          selectedValue={rule.type}
          onChange={(value: string) => {
            const rules = [...currentRules];
            if (value === "force") {
              rules[index] = {
                type: "force",
                forceId: "",
              };
            } else if (value === "organisation") {
              rules[index] = {
                type: "organisation",
                organisationIds: [],
              };
            } else {
              rules[index] = {
                type: "team",
                teamIds: [],
              };
            }
            onChangeRules(rules);
          }}
        />

        {rule.type === "force" && (
          <>
            <TextField
              name={`${namePrefix}.rlsRules.${String(index)}.forceId`}
              label="Force ID"
              value={rule.forceId}
              useFullWidth
              onChange={(value: string) => {
                const rules = [...currentRules];
                rules[index] = {
                  ...rule,
                  forceId: value.trim(),
                };
                onChangeRules(rules);
              }}
            />
            <GeographicUnitIdsField
              namePrefix={namePrefix}
              index={index}
              value={rule.geographicUnitIds ?? []}
              onChange={(ids) => {
                const rules = [...currentRules];
                rules[index] = {
                  ...rule,
                  geographicUnitIds: ids,
                };
                onChangeRules(rules);
              }}
            />
          </>
        )}

        {rule.type === "organisation" && (
          <>
            <Typeahead<{ id: string; name: string }>
              name={`rls-org-search-${keyPrefix}`}
              placeholder="Search for organisations..."
              loading={loadingOrgs}
              options={searchedOrgs.filter(
                (o) => !rule.organisationIds.includes(o.id),
              )}
              optionsKey="name"
              resetKey={rule.organisationIds.join("-")}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.name || ""
              }
              inputValue={orgSearchQuery}
              onInputChange={(value: string) => {
                const matched = searchedOrgs.find((o) => o.name === value);
                if (matched) {
                  const rules = [...currentRules];
                  rules[index] = {
                    ...rule,
                    organisationIds: [...rule.organisationIds, matched.id],
                  };
                  setOrgDetailsCache((prev) => {
                    const next = new Map(prev);
                    next.set(matched.id, { name: matched.name });
                    return next;
                  });
                  onChangeRules(rules);
                  setTimeout(() => {
                    setOrgSearchQuery("");
                    setSearchedOrgs([]);
                  }, 0);
                } else {
                  setOrgSearchQuery(value);
                }
              }}
              allowFreeText={false}
              enableFuse={false}
            />
            {rule.organisationIds.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                }}
              >
                {rule.organisationIds.map((orgId) => (
                  <Box
                    key={orgId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "action.selected",
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      {orgDetailsCache.get(orgId)?.name ?? orgId}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const rules = [...currentRules];
                        rules[index] = {
                          ...rule,
                          organisationIds: rule.organisationIds.filter(
                            (id) => id !== orgId,
                          ),
                        };
                        onChangeRules(rules);
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      ×
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {rule.type === "team" && (
          <>
            <Typeahead<{ id: string; name: string }>
              name={`rls-team-search-${keyPrefix}`}
              placeholder="Search for teams..."
              loading={loadingTeams}
              options={searchedTeams.filter(
                (t) => !rule.teamIds.includes(t.id),
              )}
              optionsKey="name"
              resetKey={rule.teamIds.join("-")}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.name || ""
              }
              inputValue={teamSearchQuery}
              onInputChange={(value: string) => {
                const matched = searchedTeams.find((t) => t.name === value);
                if (matched) {
                  const rules = [...currentRules];
                  rules[index] = {
                    ...rule,
                    teamIds: [...rule.teamIds, matched.id],
                  };
                  setTeamDetailsCache((prev) => {
                    const next = new Map(prev);
                    next.set(matched.id, { name: matched.name });
                    return next;
                  });
                  onChangeRules(rules);
                  setTimeout(() => {
                    setTeamSearchQuery("");
                    setSearchedTeams([]);
                  }, 0);
                } else {
                  setTeamSearchQuery(value);
                }
              }}
              allowFreeText={false}
              enableFuse={false}
            />
            {rule.teamIds.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                }}
              >
                {rule.teamIds.map((teamId) => (
                  <Box
                    key={teamId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "action.selected",
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      {teamDetailsCache.get(teamId)?.name ?? teamId}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const rules = [...currentRules];
                        rules[index] = {
                          ...rule,
                          teamIds: rule.teamIds.filter((id) => id !== teamId),
                        };
                        onChangeRules(rules);
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      ×
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
      <IconButton
        size="small"
        aria-label={`Remove RLS rule ${String(index + 1)}`}
        onClick={() => {
          onRemove();
        }}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  );
}

// Each dashboard bound to its stable React keys (one per panel, one per rule
// row) so the key always travels with its dashboard — no index fallback needed.
type DashboardEntry = {
  key: string;
  dashboard: EmbeddedDashboard;
  ruleKeys: string[];
};

// Build entries with a fresh key for each dashboard and rule row. Config is
// loaded from JSON without runtime validation, so legacy/malformed rows may
// carry rlsRules: null or omit it — normalise to an array so they stay editable
// rather than crashing (save-time validation still blocks invalid rules).
const buildDashboardEntries = (
  dashboards: EmbeddedDashboard[],
): DashboardEntry[] =>
  dashboards.map((dashboard) => {
    const rlsRules = Array.isArray(dashboard.rlsRules)
      ? dashboard.rlsRules
      : [];
    return {
      key: v4(),
      dashboard: { ...dashboard, rlsRules },
      ruleKeys: rlsRules.map(() => v4()),
    };
  });

function OverrideableEmbedDashboardConfiguration({
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
}) {
  const isSet = configuration.embedDashboards !== undefined;
  const inherited = inheritedConfig.embedDashboards ?? [];
  const dashboards = isSet ? (configuration.embedDashboards ?? []) : inherited;

  // Shared caches for hydrating display names of saved IDs
  const [orgDetailsCache, setOrgDetailsCache] = useState<
    Map<string, { name: string }>
  >(new Map());
  const [teamDetailsCache, setTeamDetailsCache] = useState<
    Map<string, { name: string }>
  >(new Map());

  // Single source of truth for the editable list: each dashboard bound to its
  // stable keys. Local edits update this in lockstep, so they compare equal here
  // and the effect is a no-op; a genuine external change (config load, override
  // toggle) is a wholesale replacement, so we regenerate keys rather than risk
  // pairing an old key with a new dashboard.
  const [entries, setEntries] = useState<DashboardEntry[]>(() =>
    buildDashboardEntries(dashboards),
  );
  const dashboardsSignature = JSON.stringify(dashboards);
  useEffect(() => {
    setEntries((prev) =>
      JSON.stringify(prev.map((e) => e.dashboard)) === dashboardsSignature
        ? prev
        : buildDashboardEntries(dashboards),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardsSignature]);

  // Collect all organisation/team IDs referenced by any dashboard's rules
  const allRules = dashboards.flatMap((d) => d.rlsRules);
  const allOrgIds = allRules
    .filter(
      (r): r is RlsRule & { type: "organisation" } => r.type === "organisation",
    )
    .flatMap((r) => r.organisationIds);
  const allTeamIds = allRules
    .filter((r): r is RlsRule & { type: "team" } => r.type === "team")
    .flatMap((r) => r.teamIds);

  // Hydrate organisation names for saved IDs
  useEffect(() => {
    const missingOrgIds = allOrgIds.filter((id) => !orgDetailsCache.has(id));
    if (missingOrgIds.length === 0) return;

    const hydrate = async () => {
      for (const orgId of missingOrgIds) {
        try {
          const result = await api.organisations.get(orgId);
          if (result.data) {
            setOrgDetailsCache((prev) => {
              const next = new Map(prev);
              next.set(orgId, { name: result.data.organisation.name });
              return next;
            });
          }
        } catch {
          // Organisation may have been deleted
        }
      }
    };
    void hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allOrgIds)]);

  // Hydrate team names for saved IDs
  useEffect(() => {
    const missingTeamIds = allTeamIds.filter((id) => !teamDetailsCache.has(id));
    if (missingTeamIds.length === 0) return;

    const hydrate = async () => {
      for (const teamId of missingTeamIds) {
        try {
          const result = await api.teams.get(teamId);
          if (result.data) {
            setTeamDetailsCache((prev) => {
              const next = new Map(prev);
              next.set(teamId, { name: result.data!.name });
              return next;
            });
          }
        } catch {
          // Team may have been deleted
        }
      }
    };
    void hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allTeamIds)]);

  // Apply a new entries list: update local state and push the derived
  // dashboards back to the config in one go so the two never drift.
  const commit = (next: DashboardEntry[]) => {
    setEntries(next);
    setConfiguration({
      ...configuration,
      embedDashboards: next.map((e) => e.dashboard),
    });
  };

  const updateDashboard = (di: number, patch: Partial<EmbeddedDashboard>) => {
    commit(
      entries.map((e, i) =>
        i === di ? { ...e, dashboard: { ...e.dashboard, ...patch } } : e,
      ),
    );
  };

  const addDashboard = () => {
    commit([
      ...entries,
      {
        key: v4(),
        dashboard: { supersetDashboardId: "", rlsRules: [] },
        ruleKeys: [],
      },
    ]);
  };

  const removeDashboard = (di: number) => {
    commit(entries.filter((_, i) => i !== di));
  };

  const addRule = (di: number) => {
    commit(
      entries.map((e, i) =>
        i === di
          ? {
              ...e,
              dashboard: {
                ...e.dashboard,
                rlsRules: [
                  ...e.dashboard.rlsRules,
                  { type: "force", forceId: "" },
                ],
              },
              ruleKeys: [...e.ruleKeys, v4()],
            }
          : e,
      ),
    );
  };

  const removeRule = (di: number, ruleIndex: number) => {
    commit(
      entries.map((e, i) =>
        i === di
          ? {
              ...e,
              dashboard: {
                ...e.dashboard,
                rlsRules: e.dashboard.rlsRules.filter(
                  (_, j) => j !== ruleIndex,
                ),
              },
              ruleKeys: e.ruleKeys.filter((_, j) => j !== ruleIndex),
            }
          : e,
      ),
    );
  };

  // Switching to "no" clears the rules and requires the acknowledgement
  // checkbox; switching to "yes" drops the flag so rules are required again.
  const setRlsMode = (di: number, mode: "yes" | "no") => {
    commit(
      entries.map((e, i) => {
        if (i !== di) {
          return e;
        }
        if (mode === "no") {
          return {
            ...e,
            dashboard: {
              ...e.dashboard,
              rlsRules: [],
              noRlsAcknowledged: false,
            },
            ruleKeys: [],
          };
        }
        const { noRlsAcknowledged: _omit, ...rest } = e.dashboard;
        return { ...e, dashboard: rest, ruleKeys: [] };
      }),
    );
  };

  return (
    <TableRow>
      <TableCell>Embed Dashboards (Superset)</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isSet}
              onChange={() => {
                if (!isSet) {
                  setConfiguration({
                    ...configuration,
                    embedDashboards: inherited,
                  });
                } else {
                  const { embedDashboards: _, ...rest } = configuration;
                  setConfiguration(rest);
                }
              }}
            />
          }
          label="Override"
        />
      </TableCell>
      <TableCell>
        {!isSet ? (
          <Box>
            {inherited.length > 0
              ? `${String(inherited.length)} dashboard(s) inherited`
              : "Not configured"}
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
            {entries.map(({ key, dashboard, ruleKeys }, di) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Box
                  sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
                >
                  <Box sx={{ flex: 1 }}>
                    <SupersetDashboardPicker
                      value={dashboard.supersetDashboardId}
                      onChange={(supersetDashboardId: string) => {
                        updateDashboard(di, { supersetDashboardId });
                      }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Remove dashboard ${String(di + 1)}`}
                    onClick={() => {
                      removeDashboard(di);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                <Divider sx={{ my: 1 }} />
                <FormControl>
                  <FormLabel sx={{ fontSize: "0.875rem" }}>
                    Apply row-level security (RLS)?
                  </FormLabel>
                  <RadioGroup
                    row
                    value={
                      dashboard.noRlsAcknowledged === undefined ? "yes" : "no"
                    }
                    onChange={(_e, value) => {
                      setRlsMode(di, value === "no" ? "no" : "yes");
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No — show the full dashboard to everyone"
                    />
                  </RadioGroup>
                </FormControl>

                {dashboard.noRlsAcknowledged === undefined ? (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      RLS Rules
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Configure row-level security rules. Each rule can filter
                      by force, organisation, or team. Rules of different types
                      can be mixed on the same dashboard.
                    </Typography>

                    {dashboard.rlsRules.map((rule, index) => (
                      <RlsRuleRow
                        key={ruleKeys[index]}
                        rule={rule}
                        index={index}
                        currentRules={dashboard.rlsRules}
                        onChangeRules={(rules) => {
                          updateDashboard(di, { rlsRules: rules });
                        }}
                        onRemove={() => {
                          removeRule(di, index);
                        }}
                        namePrefix={`embedDashboards.${String(di)}`}
                        keyPrefix={`${String(di)}-${String(index)}`}
                        orgDetailsCache={orgDetailsCache}
                        setOrgDetailsCache={setOrgDetailsCache}
                        teamDetailsCache={teamDetailsCache}
                        setTeamDetailsCache={setTeamDetailsCache}
                      />
                    ))}

                    <Button
                      label="Add RLS Rule"
                      startIcon={ButtonIcon.add}
                      onClick={() => {
                        addRule(di);
                      }}
                    />
                  </>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dashboard.noRlsAcknowledged ?? false}
                        onChange={(e) => {
                          updateDashboard(di, {
                            noRlsAcknowledged: e.target.checked,
                          });
                        }}
                      />
                    }
                    label="I acknowledge this dashboard will show unfiltered data to everyone in this organisation"
                  />
                )}
              </Box>
            ))}

            <Button
              label="Add Dashboard"
              startIcon={ButtonIcon.add}
              onClick={() => {
                addDashboard();
              }}
            />
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableTextFieldConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
  valueTransformer,
  multiline,
  rows,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
  valueTransformer?: (value: string) => string;
  multiline?: boolean;
  rows?: string;
}) {
  const isSet = configuration[field] !== undefined;
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isSet}
              onChange={() => {
                if (configuration[field] === undefined) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  setConfiguration(withoutOverride(configuration, field));
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        <TextField
          name={field}
          value={
            configuration[field] === undefined
              ? (inheritedConfig[field] as string)
              : (configuration[field] as string)
          }
          disabled={!isSet}
          useFullWidth
          onChange={(value: string) => {
            setConfiguration({
              ...configuration,
              [field]: valueTransformer
                ? valueTransformer(value.trim())
                : value.trim(),
            });
          }}
          multiline={multiline}
          rows={rows}
        />
      </TableCell>
    </TableRow>
  );
}

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
  },
});

function OverrideableTemplateStringConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
  multiline,
  rows,
  isBulk,
}: {
  field: keyof OrganisationConfiguration;
  label: string;
  inheritedConfig: Partial<OrganisationConfiguration>;
  configuration: Partial<OrganisationConfiguration>;
  setConfiguration: (config: Partial<OrganisationConfiguration>) => void;
  multiline?: boolean;
  rows?: string;
  isBulk?: boolean;
}) {
  const isSet = configuration[field] !== undefined;
  const exampleFormData = {
    patient: {
      givenName: "Zane",
      familyName: "Bridges",
      dateOfBirth: new Date("2001-05-31 00:00:00").toISOString(),
    },
    form: {
      typeCode: "MHA",
      name: "H3",
      dateUpdated: new Date("2024-08-22 14:55:00").toISOString(),
      section: "2",
    },
    formContext: {
      dateUpdated: new Date("2025-10-22 14:55:00").toISOString(),
      description: "Section 2",
      formNames: "A4, A4, A2, H3",
    },
    uploadDate: new Date().toISOString(),
    initialFilename: "filename.pdf",
    initialTitle: "Title",
  };

  const templateDataAvailable = [
    "patient.givenName",
    "patient.familyName",
    "patient.dateOfBirth",
    "uploadDate",
    "initialFilename",
    "initialTitle",
  ];

  if (isBulk) {
    templateDataAvailable.push("formContext.description");
    templateDataAvailable.push("formContext.dateUpdated");
    templateDataAvailable.push("formContext.formNames");
  } else {
    templateDataAvailable.push("form.typeCode");
    templateDataAvailable.push("form.name");
    templateDataAvailable.push("form.dateUpdated");
    templateDataAvailable.push("form.section");
  }

  const templateModifiersAvailable = [
    "uppercase",
    "lowercase",
    "initial",
    "redact",
    "spaceToUnderscore",
    "lastDigits",
    "dayMonthYear",
    "yearMonthDay",
    "dayMonthYearHyphen",
    "timeStamp",
  ];

  const [preview, setPreview] = useState<string>("");

  const getPreview = useCallback(
    debounce(async (template: string, templateData: TemplateData) => {
      const result = await api.templates.compileTemplate(
        template,
        templateData,
      );
      setPreview(result.data);
    }, 500),
    [],
  );

  useEffect(() => {
    if (isSet) {
      getPreview(configuration[field] as string, exampleFormData);
    } else {
      getPreview(inheritedConfig[field] as string, exampleFormData);
    }
  }, []);

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <Stack direction="row">
          <FormControlLabel
            control={
              <Switch
                checked={isSet}
                onChange={() => {
                  if (configuration[field] === undefined) {
                    setConfiguration({
                      ...configuration,
                      [field]: inheritedConfig[field],
                    });
                  } else {
                    setConfiguration(withoutOverride(configuration, field));
                    // When unchecked set the preview back to default
                    getPreview(
                      inheritedConfig[field] as string,
                      exampleFormData,
                    );
                  }
                }}
              />
            }
            label={`Override`}
          />
          <NoMaxWidthTooltip
            placement="left"
            title={
              <Stack>
                <Stack
                  direction="row"
                  divider={<Divider orientation="vertical" flexItem />}
                  spacing={2}
                >
                  <Stack>
                    <Typography fontWeight="bold">Data available</Typography>
                    {templateDataAvailable.map((data, index) => {
                      return <Typography key={index}>{data}</Typography>;
                    })}
                  </Stack>
                  <Stack>
                    <Typography fontWeight="bold">
                      Modifiers available
                    </Typography>
                    {templateModifiersAvailable.map((modifier, index) => {
                      return <Typography key={index}>{modifier}</Typography>;
                    })}
                  </Stack>
                </Stack>
                <Typography>
                  {"Example use: {{initial patient.familyName}}"}
                </Typography>
              </Stack>
            }
          >
            <IconButton>
              <Info />
            </IconButton>
          </NoMaxWidthTooltip>
        </Stack>
      </TableCell>
      <TableCell>
        <TextField
          name={field}
          multiline={multiline}
          rows={rows}
          value={
            configuration[field] === undefined
              ? (inheritedConfig[field] as string)
              : (configuration[field] as string)
          }
          disabled={!isSet}
          useFullWidth
          onChange={(value: string) => {
            setConfiguration({
              ...configuration,
              [field]: value,
            });
            getPreview(value, exampleFormData);
          }}
        />
        <Box>
          {preview &&
            preview
              .split("\n")
              .map((line, index) => (
                <Typography key={index}>{line}</Typography>
              ))}
        </Box>
      </TableCell>
    </TableRow>
  );
}

function OrganisationConfigurationEdit({
  organisationData,
  refetchOrg,
}: {
  organisationData: GetOrganisationResponse;
  refetchOrg: () => void;
}) {
  const inheritedOrganisationHierarchy =
    organisationData.configurationHierarchy.filter(
      (o) => o.organisationId !== organisationData.organisation.id,
    );

  const inheritedConfig = mergeConfigurations(
    organisationData.organisation.managingOrganisationId,
    inheritedOrganisationHierarchy,
  );

  const orgConfig = organisationData.configurationHierarchy.find(
    (o) => o.organisationId === organisationData.organisation.id,
  )!.configuration;

  const [configuration, setConfiguration] =
    useState<Partial<OrganisationConfiguration>>(orgConfig);

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Override Inherited Value</TableCell>
                <TableCell>State</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <OverrideableBooleanConfiguration
                label={"Enable Advance Choice Documents"}
                field={"acdEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Mental Capacity Act forms (FDMCA, BID)"}
                field={"mcaEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Section 62 / 62A Urgent Treatment form"}
                field={"s62Enabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={
                  "Exclude Administrative Area from Google Autosuggest Addresses"
                }
                field={"excludeAdministrativeAreaFromAddress"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable MHA Admissions"}
                field={"mhaAdmissionsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Nhs Number"}
                field={"nhsNumberEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Patient Auto-Merge Enabled"}
                field={"patientAutoMergeEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable MHA Admissions Joint Medical Recommendations"}
                field={"mhaAdmissionsJointMedRecsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Uploadable Forms (for admissions)"}
                field={"uploadFormsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Patient State"}
                field={"patientStateEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Patient List"}
                field={"patientListEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={
                  "Section 132 Information Recording (needs Patient State)"
                }
                field={"s132RecordingEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Calculate Section 136 MHA Status"}
                field={"section136MhaStatusCalculationEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Patient State CSV Report"}
                field={"mhaStatusReportEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableTextFieldConfiguration
                label={
                  "PDS End User Organisation ODS Code (must be set to enable PDS)"
                }
                field={"pdsEndUserOrganisationODSCode"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                valueTransformer={(value) => value.toUpperCase()}
              />
              <OverrideableTextFieldConfiguration
                label={
                  "Allowed PDS NHS Number (should be set in a Training org)"
                }
                field={"pdsAllowedPatientNhsNumber"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                valueTransformer={(value) => value.trim()}
              />
              <OverrideableTextFieldConfiguration
                label={
                  "Demographics Management Email Addresses (separated by commas)"
                }
                field={"patientDemographicsAlertEmail"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                multiline={true}
                rows="3"
              />
              <OverrideableBooleanConfiguration
                label={"NHS Person Demographics Service"}
                field={"pdsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"NHS Reasonable Adjustments Flag (RAF)"}
                field={"rafEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Reconciliation via NHS number"}
                field={"reconciliationViaNhsNumberEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Patient Batch Tracing Enabled"}
                field={"pdsBatchTracingEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Phonetic Patient Index Search"}
                field={"phoneticPatientIndexSearchEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableFormTemplateConfiguration
                label={"Form Templates (excl. admissions)"}
                field={"enabledForms"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableRioInstancesConfiguration
                label={"Rio Instances"}
                field={"rioInstancesEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableTppInstancesConfiguration
                label={"TPP Instances"}
                field={"tppInstancesEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableGenericInstancesConfiguration
                label={"Generic EPR Instances"}
                field={"genericInstancesEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableTextFieldConfiguration
                label={"Help Page Url"}
                field={"helpPageUrl"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableTextFieldConfiguration
                label={"Support Page Url"}
                field={"supportPageUrl"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableTemplateStringConfiguration
                label={"Rio Upload Filename Template"}
                field={"rioUploadFilenameTemplate"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableTemplateStringConfiguration
                label={"Rio Upload Title Template"}
                field={"rioUploadTitleTemplate"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableTemplateStringConfiguration
                label={"Rio Upload Description Template"}
                field={"rioUploadDescriptionTemplate"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                multiline={true}
                rows={"3"}
              />
              <OverrideableTemplateStringConfiguration
                label={"Rio Bulk Upload Filename Template"}
                field={"rioBulkUploadFilenameTemplate"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                isBulk={true}
              />
              <OverrideableTemplateStringConfiguration
                label={"Rio Bulk Upload Title Template"}
                field={"rioBulkUploadTitleTemplate"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                isBulk={true}
              />
              <OverrideableTemplateStringConfiguration
                label={"Rio Bulk Upload Description Template"}
                field={"rioBulkUploadDescriptionTemplate"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                multiline={true}
                rows={"3"}
                isBulk={true}
              />
              <OverrideableTermsAndConditionsConfiguration
                label={"Terms and Conditions"}
                field={"termsAndConditions"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableMhlAutoRouteConfiguration
                label={"MHL Auto-Route"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Dr Finder — Legacy flags (superseded by the role-targeted
                    settings below; leave as-is unless migrating)
                  </Typography>
                </TableCell>
              </TableRow>
              <OverrideableBooleanConfiguration
                label={"Enable Section 12 Doctor Finder (Legacy)"}
                field={"section12DoctorFinderEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Section 12 Payments (Legacy)"}
                field={"section12PaymentsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Precise Section 12 Payments (Legacy)"}
                field={"section12PrecisePaymentsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="subtitle2">
                    Dr Finder — role-targeted settings
                  </Typography>
                </TableCell>
              </TableRow>
              <OverrideableS12AmhpSettingsConfiguration
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableS12DoctorSettingsConfiguration
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
                isGuestOrg={organisationData.organisation.role === "guest"}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Data Controller Warning"}
                field={"dataControllerWarningEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable De Minimis Corrections"}
                field={"deMinimisCorrectionsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Redo Admission Forms (non-s15)"}
                field={"redoFormsEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Display GP Practice (from PDS)"}
                field={"gpPracticeDisplayEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Display Official Gender (from PDS)"}
                field={"officialGenderDisplayEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Display Ethnicity (from Rio)"}
                field={"rioEthnicityDisplayEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={"Enable Tortus AVT dictation"}
                field={"tortusAvtDictationEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableBooleanConfiguration
                label={
                  "Enable fake Tortus AVT (paste a note instead of the live embed)"
                }
                field={"tortusAvtFakeDictationEnabled"}
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
              <OverrideableEmbedDashboardConfiguration
                inheritedConfig={inheritedConfig}
                configuration={configuration}
                setConfiguration={setConfiguration}
              />
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Button
        label={"Update"}
        disabled={isEqual(configuration, orgConfig)}
        onClick={() => {
          const updateOrgConfig = async () => {
            function isValidConfiguration(
              config: OrganisationConfiguration,
              configOnThisOrg: Partial<OrganisationConfiguration>,
            ): string[] {
              const errors: string[] = [];

              if (config.pdsEnabled) {
                if (!config.pdsEndUserOrganisationODSCode) {
                  errors.push(
                    "PDS requires PDS End User Organisation ODS Code to be set",
                  );
                }

                if (!config.nhsNumberEnabled) {
                  errors.push("PDS requires NHS Number to be enabled");
                }
              }

              if (config.reconciliationViaNhsNumberEnabled) {
                if (!config.nhsNumberEnabled) {
                  errors.push(
                    "Reconciliation via NHS number requires NHS Number to be enabled",
                  );
                }

                if (!config.pdsEnabled) {
                  errors.push(
                    "Reconciliation via NHS number requires PDS to be enabled",
                  );
                }
              }

              if (config.mhaStatusReportEnabled) {
                if (!config.patientStateEnabled) {
                  errors.push(
                    "Patient State CSV Report requires Patient State to be enabled",
                  );
                }
              }

              if (config.pdsBatchTracingEnabled) {
                if (!config.pdsEnabled) {
                  errors.push("PDS Batch Tracing requires PDS to be enabled");
                }

                if (
                  configOnThisOrg.pdsBatchTracingEnabled &&
                  organisationData.organisation.dataControllerId !==
                    organisationData.organisation.id
                ) {
                  errors.push(
                    "PDS Batch Tracing can only be enabled on the data controller organisation",
                  );
                }
              }

              if (config.section12PrecisePaymentsEnabled) {
                if (!config.section12PaymentsEnabled) {
                  errors.push(
                    "Precise Section 12 Payments requires Section 12 Payments to be enabled",
                  );
                }
              }

              if (config.section12AmhpPrecisePaymentsEnabled) {
                if (!config.section12AmhpApprovePaymentsEnabled) {
                  errors.push(
                    "Dr Finder AMHP 'Precise payment fees' requires 'Approve Payment Requests' to be enabled",
                  );
                }
              }

              configOnThisOrg.embedDashboards?.forEach((d, index) => {
                if (!EmbeddedDashboardSchema.safeParse(d).success) {
                  errors.push(
                    `Dashboard ${String(index + 1)} must have a valid RLS rule, or acknowledge no filtering`,
                  );
                }
              });

              return errors;
            }

            const configErrors = isValidConfiguration(
              {
                ...inheritedConfig,
                ...configuration,
              },
              configuration,
            );

            if (configErrors.length > 0) {
              renderErrorToast({
                message: `Invalid configuration: ${configErrors.join(", ")}`,
              });
              return;
            }

            const result =
              await api.configuration.updateOrganisationConfiguration(
                organisationData.organisation.id,
                configuration,
              );

            if (result.status === 204) {
              renderSuccessToast({
                message: "Configuration updated successfully",
              });
              refetchOrg();
            } else if (result.status === 400) {
              renderErrorToast({
                message: `Error updating configuration: ${result.data.reason}`,
              });
            } else {
              renderErrorToast({ message: "Error updating configuration" });
            }
          };
          void updateOrgConfig();
        }}
      />
    </>
  );
}

function OrgMemberRow({
  member,
  organisationId,
  refetchOrg,
}: {
  organisationId: string;
  member: {
    role: OrganisationMembership["role"];
    userId: UUID;
    userName: string;
    userEmail: string;
  };
  refetchOrg: () => void;
}) {
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  const options: MenuOptionsType[] = [
    {
      name: "Remove from Org",
      onClick: () => {
        setConfirmFn({
          confirmFn: async () => {
            await api.organisations.removeMember(organisationId, {
              userId: member.userId,
              role: member.role,
            });

            refetchOrg();
          },
          message: `Are you sure you want to remove ${member.userName} from this organisation?`,
        });
      },
      icon: "delete",
      disabled: false,
    },
  ];

  return (
    <TableRow key={member.userId}>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => {
            setConfirmFn(null);
          }}
        />
      )}
      <TableCell>{member.userName}</TableCell>
      <TableCell>{member.userEmail}</TableCell>
      <TableCell>{getRoleLabel(member.role)}</TableCell>

      <TableCell>
        <Box
          ref={cardRef}
          onClick={() => {
            setIsMenuOpen(true);
          }}
        >
          <MenuFlyout
            cardRef={cardRef}
            options={options}
            isOpen={isMenuOpen}
            onClose={() => {
              setIsMenuOpen(false);
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

function OrgMembersEdit({
  organisation,
  organisationId,
  refetchOrg,
}: {
  organisation: ExtendedOrganisation | null;
  organisationId: string;
  refetchOrg: () => void;
}) {
  const { t } = useTranslation();

  const [searchResults, setSearchResults] = useState<ExtendedThalamosUser[]>(
    [],
  );

  const [userValue, setUserValue] = useState<string | ExtendedThalamosUser>("");

  const [selectedRole, setSelectedRole] = useState("");

  const handleRoleChange = async (role: OrganisationMembership["role"]) => {
    if (typeof userValue !== "string" && role) {
      await api.organisations.addMember(organisationId, {
        userId: userValue.id,
        role: role,
      });
      refetchOrg();
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", width: "100%" }}>
        <Typeahead
          css={css`
            width: 500px;
          `}
          label={"Search for a user to add"}
          options={searchResults}
          name="recipientEmail"
          enableFuse={false}
          inputValue={
            typeof userValue === "string" ? userValue : userValue?.email
          }
          optionsKey="name"
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : `${option.name} (${option.email})`
          }
          onInputChange={async (value: string) => {
            const match = searchResults.find(
              (u) => value === `${u.name} (${u.email})`,
            );
            if (match) {
              setUserValue(match);
            } else {
              setUserValue(value);
            }
            if (value.length > 1) {
              const results = await api.users.search(value, 10, 0);

              const filtered = results.data?.results.filter(
                (u) => !isGuestUser(u),
              );

              setSearchResults(filtered);

              if (match) {
                setUserValue(match);
              }
            } else {
              setSearchResults([]);
            }
          }}
        />
        <Box sx={{ marginLeft: "1em", width: "20%" }}>
          <Dropdown
            name="add-member-dropdown"
            values={addOrgMemberValues}
            selectedValue={selectedRole}
            onChange={(e) => {
              setSelectedRole(e);
              handleRoleChange(e);
            }}
            placeholder="Select role"
            disabled={typeof userValue === "string"}
          />
        </Box>
      </Box>

      <TableContainer sx={{ width: "100%" }}>
        <TableRow>
          <TableCell>
            <strong>{t("common.name")}</strong>
          </TableCell>
          <TableCell>
            <strong>{t("common.email")}</strong>
          </TableCell>
          <TableCell>
            <strong>{t("common.role")}</strong>
          </TableCell>
        </TableRow>
        {(organisation?.members || []).map((member) => {
          return (
            <OrgMemberRow
              member={member}
              organisationId={organisationId}
              refetchOrg={refetchOrg}
            />
          );
        })}
      </TableContainer>
    </>
  );
}

function OrganisationTree({ orgTree }: { orgTree: OrgTree }) {
  const dataControllerColors = [
    "#42d4f4", // cyan
    "#ffe119", // yellow
    "#3cb44b", // green
    "#f58231", // orange
    "#4363d8", // blue
    "#808000", // olive
    "#000075", // navy
    "#f032e6", // magenta
    "#e6194B", // red
    "#911eb4", // purple
    "#aaffc3", // mint
    "#469990", // teal
    "#9A6324", // brown
    "#bfef45", // lime
    "#800000", // maroon
    "#dcbeff", // lavender
  ];

  function generateRandomColor() {
    return (
      "hsl(" +
      360 * Math.random() +
      "," +
      (80 + 15 * Math.random()) +
      "%," +
      (50 + 5 * Math.random()) +
      "%)"
    );
  }

  const dataControllerToColor = new Map<string, string>();

  function getDataControllerColor(dataControllerId: string) {
    if (!dataControllerToColor.has(dataControllerId)) {
      dataControllerToColor.set(
        dataControllerId,
        dataControllerColors[dataControllerToColor.size] ??
          generateRandomColor(),
      );
    }
    return dataControllerToColor.get(dataControllerId);
  }

  return (
    <Box>
      {orgTree.map((org) => (
        <OrgSubTree
          org={org}
          depth={0}
          getDataControllerColor={getDataControllerColor}
        />
      ))}
    </Box>
  );
}

const CloudIcon = ({ title }: { title: string }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "grid", marginLeft: 1 }}>
      <Cloud
        sx={{
          gridColumnStart: 1,
          gridColumnEnd: 1,
          gridRowStart: 1,
          gridRowEnd: 1,
        }}
      />
      <Typography
        sx={{
          gridColumnStart: 1,
          gridColumnEnd: 1,
          gridRowStart: 1,
          gridRowEnd: 1,
          alignSelf: "center",
          justifySelf: "center",
        }}
        color={theme.palette.common.background}
        variant="overline"
        fontSize={"0.5rem"}
        height={"1rem"}
      >
        {title}
      </Typography>
    </Box>
  );
};

type OrgTreeChild = {
  label: string;
  value: string;
  type: "team" | "organisation";
  dataControllerId?: string;
  isUserManagementOrganisation?: boolean;
  config?: Partial<OrganisationConfiguration>;
  children: OrgTreeChild[];
};

function OrgSubTree({
  org,
  depth,
  getDataControllerColor,
  parentColor,
}: {
  org: OrgTreeChild;
  depth: number;
  getDataControllerColor: (dataControllerId: string) => string | undefined;
  parentColor?: string;
}) {
  const color =
    org.type === "organisation"
      ? getDataControllerColor(org.dataControllerId ?? "")
      : parentColor;

  const navigate = useNavigate();

  // The badge names the instance, so it is only meaningful for a lone one.
  const rioInstances = org.config?.rioInstancesEnabled;
  const onlyRioInstance =
    rioInstances?.length === 1 ? rioInstances[0] : undefined;

  return (
    <Box sx={{ marginLeft: depth * 2 }}>
      <Box sx={{ display: "flex" }}>
        {org.type === "organisation" ? (
          <FiberManualRecord sx={{ color: color }} />
        ) : (
          <PlayArrow sx={{ color: color }} />
        )}
        <Link
          underline="none"
          color="inherit"
          sx={{
            fontWeight: depth === 0 ? "bold" : "",
            textDecoration:
              org.value === org.dataControllerId ? `underline ${color}` : "",
          }}
          onClick={() => {
            org.type === "organisation"
              ? navigate(routeFns.organisationCreateEdit(org.value))
              : navigate(routeFns.teamCreateEdit(org.value));
          }}
        >
          {org.label}
        </Link>
        {org.isUserManagementOrganisation && (
          <Tooltip title="User Management Org">
            <Groups sx={{ marginLeft: 1 }} />
          </Tooltip>
        )}
        {onlyRioInstance !== undefined && (
          <Tooltip title={onlyRioInstance.displayName}>
            <CloudIcon title={"Rio"} />
          </Tooltip>
        )}
        {org.config?.pdsEnabled && (
          <Tooltip title={"PDS"}>
            <CloudIcon title={"PDS"} />
          </Tooltip>
        )}
      </Box>
      {org.children.map((child) => (
        <OrgSubTree
          org={child}
          depth={depth + 1}
          getDataControllerColor={getDataControllerColor}
          parentColor={color}
        />
      ))}
    </Box>
  );
}

function OrganisationCreateEditPageInner({
  organisationData,
  organisationId,
  refetchOrg,
  tab,
  setTab,
  orgTree,
}: {
  organisationId: string;
  organisationData: GetOrganisationResponse | null;
  refetchOrg: () => void;
  tab: number;
  setTab: (tab: number) => void;
  orgTree: OrgTree;
}) {
  const organisation = organisationData?.organisation || null;

  const isUserManagementOrganisation =
    organisationData?.organisation.isUserManagementOrganisation ?? false;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {organisationData ? (
        <h2>
          {organisation?.name} ({organisation?.role}){" "}
        </h2>
      ) : (
        <h2>Create New Organisation</h2>
      )}

      <Box sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => {
            setTab(v);
          }}
          variant="scrollable"
        >
          <Tab key={"Basic Details"} label={"Basic Details"} />
          <Tab
            key={"Child Orgs / Teams"}
            label={
              organisation && isCqc(organisation)
                ? `Manage Soads (${
                    organisation?.childOrganisations.length || 0
                  })`
                : `Children (${
                    organisation?.childOrganisations.length || 0
                  }) / Teams (${organisation?.teams.length || 0})`
            }
            disabled={!organisationData}
          />
          <Tab
            key={"Organisation Members"}
            label={`Members (${organisation?.members.length || 0})`}
            disabled={!organisationData}
          />
          <Tab
            key={"Data Controller"}
            label={`Data Controller (${
              organisation?.orgsInThisDataController.length || 0
            })`}
            disabled={!organisationData}
          />
          <Tab
            key={"Configuration"}
            label={"Configuration"}
            disabled={!organisationData}
          />
          <Tab
            key={"Org Tree"}
            label={"Org Tree"}
            disabled={!organisationData || !orgTree}
          />
          {isUserManagementOrganisation && (
            <Tab key={"Users"} label={"Users"} />
          )}
        </Tabs>
      </Box>
      {tab === 0 && (
        <OrganisationCreateEditForm
          organisation={organisation}
          organisationId={organisationId}
          refetchOrg={refetchOrg}
        />
      )}

      {tab === 1 &&
        (organisation && isCqc(organisation) ? (
          <SoadManagement
            refetchOrg={refetchOrg}
            organisationData={organisationData!}
            organisation={organisation}
          />
        ) : (
          <ChildOrgsAndTeamsEdit
            organisationData={organisationData!}
            organisation={organisation!}
          />
        ))}

      {tab === 2 && (
        <OrgMembersEdit
          organisation={organisation}
          organisationId={organisationId}
          refetchOrg={refetchOrg}
        />
      )}

      {tab === 3 && (
        <DataControllerInfo
          organisationData={organisationData!}
          organisation={organisation!}
        />
      )}

      {tab === 4 && (
        <OrganisationConfigurationEdit
          organisationData={organisationData!}
          refetchOrg={refetchOrg}
        />
      )}

      {tab === 5 && <OrganisationTree orgTree={orgTree} />}

      {tab === 6 && isUserManagementOrganisation && (
        <ManageOrganisationUsers
          organisationData={organisationData!}
          refetchOrg={refetchOrg}
        />
      )}
    </Box>
  );
}

export function OrganisationCreateEdit({}: {}) {
  const { organisationId } = useParams();

  const [tab, setTab] = useState(0);

  const [{ data: organisation, loading: orgLoading, response }, refetchOrg] =
    apiHooks.organisations.get(organisationId!);

  const [{ data: orgsAndTeams, loading: orgsAndTeamsLoading }] =
    apiHooks.reports.availableOrgsAndTeams({ orgId: organisationId! });

  return orgLoading || orgsAndTeamsLoading ? (
    <></>
  ) : (
    <OrganisationCreateEditPageInner
      tab={tab}
      setTab={setTab}
      organisationId={organisationId!}
      organisationData={response?.status === 200 ? organisation! : null}
      refetchOrg={refetchOrg}
      orgTree={orgsAndTeams!.orgTree}
    />
  );
}

const changeDataControllerConfirmationSchema = object({
  ccmTicket: nonEmptyString.required("Please provide a Jira CCM ticket"),
  approverOne: nonEmptyString.required("Please provide a name"),
  approverTwo: nonEmptyString.required("Please provide a name"),
});

function ChangeDataControllerConfirmationDialog({
  open,
  onClose,
}: DialogProps<
  unknown,
  | ({ continue: true } & InferType<
      typeof changeDataControllerConfirmationSchema
    >)
  | { continue: false }
>) {
  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    void onClose({
      continue: false,
    });
  }, [onClose]);

  const handleContinue = useCallback(
    (values: InferType<typeof changeDataControllerConfirmationSchema>) => {
      void onClose({
        continue: true,
        ...values,
      });
    },
    [onClose],
  );

  return (
    <PopupDialog open={open} onClose={handleClose}>
      <PopupDialogTitle
        closeDialog={handleClose}
        titleText={t(
          "pages.organisationCreateEdit.changeDataControllerConfirmationDialog.title",
        )}
      />
      <Stack justifyContent={"center"} alignItems="center" gap={1}>
        <Banner
          bannerType={BannerList.WARNING}
          body={t(
            "pages.organisationCreateEdit.changeDataControllerConfirmationDialog.heading",
          ).split("\n")}
        />
        <Formik
          initialValues={{
            approverOne: "",
            approverTwo: "",
            ccmTicket: "",
          }}
          validationSchema={changeDataControllerConfirmationSchema}
          onSubmit={(values) => {
            handleContinue(values);
          }}
        >
          {(formikData) => {
            const { submitForm } = formikData;

            const fieldProps = {
              ...formikData,
              validationSchema: changeDataControllerConfirmationSchema,
              context: {},
            };

            return (
              <Stack sx={{ width: "100%", padding: "1rem" }}>
                <TextboxFormField
                  field={{
                    type: "textbox",
                    label: "CCM Ticket",
                    field: "ccmTicket",
                  }}
                  fieldProps={fieldProps}
                />
                <TextboxFormField
                  field={{
                    type: "textbox",
                    label: "Approver 1",
                    field: "approverOne",
                  }}
                  fieldProps={fieldProps}
                />
                <TextboxFormField
                  field={{
                    type: "textbox",
                    label: "Approver 2",
                    field: "approverTwo",
                  }}
                  fieldProps={fieldProps}
                />

                <DialogActions
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignSelf: "normal",
                  }}
                >
                  <Button
                    label={t("buttonLabels.cancel")}
                    color="error"
                    variant="outlined"
                    onClick={handleClose}
                  />
                  <Button
                    color="primary"
                    onClick={() => {
                      void submitForm();
                    }}
                    label={t("buttonLabels.continue")}
                  />
                </DialogActions>
              </Stack>
            );
          }}
        </Formik>
      </Stack>
    </PopupDialog>
  );
}
